import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, FileText, MessageSquare, Hash, Phone, AlertCircle, CheckCircle,
  RotateCcw, Copy, Info, Eye, Zap, Shield, X, ClipboardPaste, Loader2, Type,
  CheckCircle2, XCircle, Smartphone, Wifi, Battery, Signal
} from 'lucide-react';
import api from '../services/api';
import SearchableDropdown from '../components/common/SearchableDropdown';

// DLT approved variable tags
const DLT_VARIABLE_TAGS = [
  { tag: '{#var#}', label: 'General', description: 'Any text value', color: '#d97706' },
  { tag: '{#numeric#}', label: 'Numeric', description: 'OTPs, amounts', color: '#dc2626' },
  { tag: '{#alphanumeric#}', label: 'Alphanumeric', description: 'Reference IDs', color: '#7c3aed' },
  { tag: '{#url#}', label: 'URL', description: 'Website links', color: '#2563eb' },
  { tag: '{#urlott#}', label: 'URL OTT', description: 'App links', color: '#0891b2' },
  { tag: '{#cbn#}', label: 'Callback', description: 'Callback numbers', color: '#059669' },
  { tag: '{#email#}', label: 'Email', description: 'Email addresses', color: '#db2777' },
];

const DLT_VAR_REGEX = /\{#(?:var|numeric|alphanumeric|url|urlott|cbn|email)#\}/g;

// ─── Mobile Phone Preview ───
const MobilePreview = ({ message, senderName, encodingType, smsCount, charCount }) => {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const hasMessage = message && message.trim().length > 0;
  const hasUnfilledVars = DLT_VAR_REGEX.test(message || '');

  return (
    <div className="flex flex-col items-center">
      {/* Phone Frame */}
      <div className="relative" style={{ width: '260px' }}>
        <div className="rounded-[2rem] p-[3px]" style={{
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}>
          <div className="rounded-[1.8rem] overflow-hidden" style={{ background: '#000', border: '2px solid #2a2a3e' }}>
            {/* Dynamic Island */}
            <div className="flex justify-center pt-2 pb-1" style={{ background: '#000' }}>
              <div className="w-[80px] h-[24px] rounded-full flex items-center justify-center gap-2" style={{ background: '#111', border: '1px solid #222' }}>
                <div className="w-[7px] h-[7px] rounded-full" style={{ background: '#1a1a2e', border: '1px solid #333' }} />
                <div className="w-[5px] h-[5px] rounded-full" style={{ background: '#0a84ff' }} />
              </div>
            </div>
            {/* Status Bar */}
            <div className="flex items-center justify-between px-4 py-1" style={{ background: '#000' }}>
              <span className="text-[10px] font-semibold text-white/90">{timeStr}</span>
              <div className="flex items-center gap-1">
                <Signal className="w-2.5 h-2.5 text-white/70" />
                <Wifi className="w-2.5 h-2.5 text-white/70" />
                <Battery className="w-3 h-3 text-white/70" />
              </div>
            </div>
            {/* Messages Header */}
            <div className="px-3 py-2 flex items-center gap-2" style={{ background: 'linear-gradient(180deg, #f8f9fa 0%, #f0f1f3 100%)', borderBottom: '1px solid #e0e0e0' }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                <span className="text-[10px] font-bold text-white">{(senderName || 'SM').substring(0, 2).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-gray-900 truncate">{senderName || 'Sender'}</p>
                <p className="text-[9px] text-gray-500">SMS</p>
              </div>
            </div>
            {/* Message Area */}
            <div className="px-3 py-3" style={{ background: 'linear-gradient(180deg, #e8ecf0 0%, #dfe3e8 100%)', minHeight: '260px', maxHeight: '260px', overflowY: 'auto' }}>
              {hasMessage ? (
                <div className="flex justify-start">
                  <div className="max-w-[200px]">
                    <div className="flex justify-center mb-2">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-medium" style={{ background: 'rgba(0,0,0,0.06)', color: '#8e8e93' }}>Today {timeStr}</span>
                    </div>
                    <div className="px-2.5 py-2 rounded-2xl rounded-tl-md text-[12px] leading-[1.35] break-words" style={{ background: '#fff', color: '#1c1c1e', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                      {hasUnfilledVars ? (
                        message.split(DLT_VAR_REGEX).map((part, i) => {
                          const tags = message.match(DLT_VAR_REGEX) || [];
                          const elements = [<span key={`p-${i}`}>{part}</span>];
                          if (i < tags.length) {
                            elements.push(
                              <span key={`v-${i}`} className="inline-block px-1 py-0.5 mx-0.5 rounded text-[9px] font-bold" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', border: '1px dashed rgba(245, 158, 11, 0.4)' }}>___</span>
                            );
                          }
                          return elements;
                        })
                      ) : message}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 px-1">
                      <span className="text-[8px] font-medium px-1 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.05)', color: '#8e8e93' }}>{charCount} chars</span>
                      <span className="text-[8px] font-medium px-1 py-0.5 rounded-full" style={{ background: smsCount > 1 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0,0,0,0.05)', color: smsCount > 1 ? '#d97706' : '#8e8e93' }}>{smsCount} SMS</span>
                      <span className="text-[8px] font-medium px-1 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.05)', color: '#8e8e93' }}>{encodingType}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full pt-12 text-center">
                  <MessageSquare className="w-5 h-5 mb-2" style={{ color: '#c7c7cc' }} />
                  <p className="text-[11px]" style={{ color: '#8e8e93' }}>No message yet</p>
                </div>
              )}
            </div>
            <div className="flex justify-center py-1.5" style={{ background: '#e8ecf0' }}>
              <div className="w-[100px] h-[3px] rounded-full" style={{ background: 'rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Required Field Label ───
const RequiredLabel = ({ icon: Icon, text, step }) => (
  <label className="flex items-center gap-2 text-xs font-bold text-gray-600 mb-2 uppercase tracking-wider">
    <span className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>{step}</span>
    <Icon className="w-3.5 h-3.5" style={{ color: '#4f46e5' }} />
    {text}
    <span className="text-red-400 text-sm">*</span>
  </label>
);

const SendSMSPro = () => {
  // ─── State ───
  const [headers, setHeaders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedHeader, setSelectedHeader] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [originalTemplate, setOriginalTemplate] = useState('');
  const [editableMessage, setEditableMessage] = useState('');
  const [encodingType, setEncodingType] = useState('GSM');
  const [variables, setVariables] = useState([]);
  const [variableValues, setVariableValues] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState('');
  const [validNumbers, setValidNumbers] = useState([]);
  const [invalidNumbers, setInvalidNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingHeaders, setLoadingHeaders] = useState(false);
  const [headersError, setHeadersError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState('');
  const [pasteToast, setPasteToast] = useState({ show: false, type: '', message: '' });
  const overlayRef = useRef(null);

  // ─── Load headers on mount ───
  useEffect(() => { loadHeaders(); }, []);

  // ─── Auto-detect encoding ───
  useEffect(() => {
    if (!editableMessage) return;
    const gsmChars = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !"#¤%&'()*+,\-.\/0-9:;<=>?¡A-ZÄÖÑÜa-zäöñüà§^{}\\\[~\]|€\s]*$/;
    setEncodingType(gsmChars.test(editableMessage) ? 'GSM' : 'UCS-2');
  }, [editableMessage]);

  // ─── API calls ───
  const loadHeaders = async () => {
    setLoadingHeaders(true);
    setHeadersError('');
    try {
      const response = await api.get('/user/headers');
      if (response.data.success && response.data.headers) {
        setHeaders(response.data.headers);
        if (response.data.headers.length === 0) setHeadersError('No headers mapped to your account.');
      } else {
        setHeaders([]);
        setHeadersError('No headers available');
      }
    } catch (err) {
      setHeadersError(err.response?.data?.error || 'Unable to fetch headers.');
      setHeaders([]);
    } finally {
      setLoadingHeaders(false);
    }
  };

  const loadTemplates = async (headerId) => {
    if (!headerId) { setTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const response = await api.get(`/user/headers/${headerId}/templates`);
      if (response.data.success && response.data.templates) {
        setTemplates(response.data.templates);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ─── Handlers ───
  const handleHeaderChange = (value) => {
    setSelectedHeader(value);
    setSelectedTemplate('');
    setTemplateId(''); setTemplateName(''); setTemplateType('');
    setOriginalTemplate(''); setEditableMessage('');
    setTemplates([]); setVariables([]); setVariableValues([]);
    setPasteText(''); setPasteError('');
    if (value) loadTemplates(value);
  };

  const handleTemplateChange = async (value) => {
    setSelectedTemplate(value);
    if (!value) {
      setTemplateId(''); setTemplateName(''); setTemplateType('');
      setOriginalTemplate(''); setEditableMessage('');
      setVariables([]); setVariableValues([]);
      setPasteText(''); setPasteError('');
      return;
    }
    try {
      const response = await api.get(`/user/templates/${value}`);
      if (response.data.success && response.data.template) {
        const content = response.data.template.template_content;
        setTemplateId(response.data.template.template_id);
        setTemplateName(response.data.template.template_name || '');
        setTemplateType(response.data.template.template_type || '');
        setOriginalTemplate(content);
        setEditableMessage(content);
        setPasteText(content);
        const matches = content.match(DLT_VAR_REGEX) || [];
        const varObjects = matches.map((tag, idx) => {
          const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
          return { tag, index: idx, ...tagInfo };
        });
        setVariables(varObjects);
        setVariableValues(Array(matches.length).fill(''));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load template content');
    }
  };

  const generatePreviewMessage = useCallback((values) => {
    let preview = originalTemplate;
    let vi = 0;
    preview = preview.replace(DLT_VAR_REGEX, () => {
      const replacement = values[vi] || '{#var#}';
      vi++;
      return replacement;
    });
    return preview;
  }, [originalTemplate]);

  const handleVariableChange = (index, value) => {
    if (value.length > 30) return;
    const newValues = [...variableValues];
    newValues[index] = value;
    setVariableValues(newValues);
    const preview = generatePreviewMessage(newValues);
    setEditableMessage(preview);
    setPasteText(preview);
  };

  const handleResetVariables = () => {
    setEditableMessage(originalTemplate);
    setVariableValues(Array(variables.length).fill(''));
    setPasteText(originalTemplate);
    setPasteError('');
  };

  // ─── Paste extraction ───
  const extractVariablesFromText = (text) => {
    if (!text?.trim()) return { success: false, error: 'No text to process' };
    if (!originalTemplate) return { success: false, error: 'Select a template first' };
    const parts = originalTemplate.split(DLT_VAR_REGEX);
    const varCount = parts.length - 1;
    if (varCount === 0) {
      return text.trim() === originalTemplate.trim()
        ? { success: true, values: [], static: true }
        : { success: false, error: 'Static template — no variables to fill' };
    }
    const regexParts = parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regexStr = '^' + regexParts.join('(.+?)') + '$';
    let regex;
    try { regex = new RegExp(regexStr, 's'); } catch { return { success: false, error: 'Template pattern error' }; }
    const match = text.trim().match(regex);
    if (!match) return { success: false, error: 'Message doesn\'t match template pattern' };
    const extracted = [];
    for (let i = 1; i <= varCount; i++) {
      const val = match[i] || '';
      extracted.push(val);
      if (val.length > 30) return { success: false, error: 'Variable values exceed 30 char limit' };
    }
    return { success: true, values: extracted };
  };

  const handlePasteTextChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    setPasteError('');
    if (!originalTemplate || variables.length === 0) {
      setEditableMessage(text);
      return;
    }
    const result = extractVariablesFromText(text);
    if (result.success && result.values.length > 0) {
      setVariableValues(result.values);
      setEditableMessage(generatePreviewMessage(result.values));
    } else {
      setEditableMessage(text);
    }
  };

  const handleDirectPaste = (e) => {
    if (!originalTemplate || variables.length === 0) return;
    const pasted = e.clipboardData?.getData('text');
    if (!pasted || e.target.tagName === 'INPUT') return;
    e.preventDefault();
    const result = extractVariablesFromText(pasted);
    if (result.success && result.values.length > 0) {
      setVariableValues(result.values);
      setEditableMessage(generatePreviewMessage(result.values));
      setPasteToast({ show: true, type: 'success', message: `${result.values.length} variable${result.values.length > 1 ? 's' : ''} extracted` });
    } else {
      setPasteToast({ show: true, type: 'error', message: result.error || 'Could not extract variables' });
    }
    setTimeout(() => setPasteToast({ show: false, type: '', message: '' }), 3000);
  };

  // ─── Mobile number processing ───
  const processMobileNumbers = (numbersText) => {
    const numbers = numbersText.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
    const numericRegex = /^[0-9]+$/;
    const indianMobileRegex = /^(\+91|91)?[6-9]\d{9}$/;
    const validSet = new Set();
    const invalid = [];
    numbers.forEach(num => {
      const cleaned = num.replace(/[\s-]/g, '');
      if (!numericRegex.test(cleaned.replace(/^\+91|^91/, ''))) {
        if (!invalid.includes(num)) invalid.push(num);
      } else if (indianMobileRegex.test(cleaned)) {
        let normalized = cleaned;
        if (normalized.startsWith('+91')) normalized = normalized.substring(3);
        else if (normalized.startsWith('91') && normalized.length > 10) normalized = normalized.substring(2);
        validSet.add(normalized);
      } else {
        if (!invalid.includes(num)) invalid.push(num);
      }
    });
    return { valid: Array.from(validSet), invalid };
  };

  const handleMobileNumbersChange = (e) => {
    const text = e.target.value;
    setMobileNumbers(text);
    const { valid, invalid } = processMobileNumbers(text);
    setValidNumbers(valid);
    setInvalidNumbers(invalid);
    if (validationErrors.mobile) setValidationErrors(prev => ({ ...prev, mobile: '' }));
  };

  // ─── Character stats ───
  const calculateCharacterStats = () => {
    const length = editableMessage.length;
    const isUnicode = encodingType === 'UCS-2';
    let smsCount = 1;
    if (isUnicode) smsCount = length <= 70 ? 1 : Math.ceil(length / 67);
    else smsCount = length <= 160 ? 1 : Math.ceil(length / 153);
    return { length, smsCount };
  };

  const stats = calculateCharacterStats();

  // ─── Submit ───
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitResult(null); setResponseTime(null);

    const errors = {};
    if (!selectedHeader) errors.header = 'Select a header';
    if (!selectedTemplate) errors.template = 'Select a template';
    if (!editableMessage.trim()) errors.message = 'Message is empty';
    if (DLT_VAR_REGEX.test(editableMessage)) errors.variables = 'Fill all variables';
    if (validNumbers.length === 0) errors.mobile = 'Add at least one valid number';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    const startTime = performance.now();
    try {
      const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
      const response = await api.post('/user/send-sms', {
        to: validNumbers.join(','),
        message: editableMessage,
        entity_id: selectedHeaderObj?.entity_id || '',
        header_id: selectedHeaderObj?.header_name || '',
        template_id: templateId,
        encoding_type: encodingType,
        message_type: 'transactional',
        channel: 'GUI',
      });
      const elapsed = Math.round(performance.now() - startTime);
      setResponseTime(elapsed);

      if (response.data.status !== 'accepted') {
        setError(response.data?.message || 'Failed to submit SMS.');
        setLoading(false);
        return;
      }
      setSubmitResult(response.data);
      const count = response.data.count || response.data.message_ids?.length || 1;
      setSuccess(`${count} SMS accepted in ${elapsed}ms`);
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait and try again.');
      } else {
        const msg = errData?.message || 'Unable to send SMS.';
        const refId = errData?.reference_id || '';
        setError(refId ? `${msg} (Ref: ${refId})` : msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Full reset ───
  const handleFullReset = () => {
    setSelectedHeader(''); setSelectedTemplate('');
    setTemplateId(''); setTemplateName(''); setTemplateType('');
    setOriginalTemplate(''); setEditableMessage(''); setVariables([]); setVariableValues([]);
    setMobileNumbers(''); setValidNumbers([]); setInvalidNumbers([]);
    setValidationErrors({}); setError(''); setSuccess(''); setSubmitResult(null);
    setPasteText(''); setPasteError('');
  };

  // ─── Derived ───
  const selectedHeaderObj = headers.find(h => h.id === parseInt(selectedHeader));
  const noUnfilledVars = !!editableMessage && !DLT_VAR_REGEX.test(editableMessage);

  const validationChecks = [
    { label: 'Header', done: !!selectedHeader },
    { label: 'Template', done: !!selectedTemplate },
    { label: 'Message', done: noUnfilledVars },
    { label: 'Recipients', done: validNumbers.length > 0 },
  ];
  const readyCount = validationChecks.filter(c => c.done).length;
  const allReady = readyCount === validationChecks.length;

  const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-7xl mx-auto">
      {/* ─── Page Header ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Send className="w-5 h-5 text-white" />
            </div>
            Send SMS
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>Pro</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Fill 4 required fields and send — Header, Template, Message, Recipients</p>
        </div>
        {/* Readiness indicator */}
        <div className="flex items-center gap-2">
          {validationChecks.map((c, i) => (
            <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase" style={{
              background: c.done ? 'rgba(16, 185, 129, 0.08)' : 'rgba(99, 102, 241, 0.04)',
              color: c.done ? '#059669' : '#94a3b8',
              border: c.done ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid rgba(99, 102, 241, 0.08)',
            }}>
              {c.done ? <CheckCircle2 className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full" style={{ border: '1.5px solid #cbd5e1' }} />}
              {c.label}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Success Result ─── */}
      {success && submitResult && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.2)' }}>
          <div className="px-5 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white">SMS Submitted Successfully</span>
            </div>
            <div className="flex items-center gap-3">
              {responseTime !== null && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                  <Zap className="w-3 h-3" /> {responseTime}ms
                </span>
              )}
              <button type="button" onClick={() => { setSuccess(''); setSubmitResult(null); }} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4" style={{ background: 'rgba(16, 185, 129, 0.03)' }}>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Count</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{submitResult.count || 0}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Campaign ID</p>
              <p className="text-xs font-mono text-gray-700 mt-1.5 truncate">{submitResult.campaign_id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reference ID</p>
              <div className="flex items-center gap-1 mt-1.5">
                <p className="text-xs font-mono text-gray-700 truncate">{submitResult.reference_id || 'N/A'}</p>
                {submitResult.reference_id && (
                  <button type="button" onClick={() => navigator.clipboard.writeText(submitResult.reference_id)} className="text-gray-400 hover:text-indigo-500 flex-shrink-0"><Copy className="w-3 h-3" /></button>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Response</p>
              <p className="text-xs font-mono mt-1.5" style={{ color: responseTime < 50 ? '#059669' : responseTime < 200 ? '#d97706' : '#dc2626' }}>
                {responseTime !== null ? `${responseTime}ms` : 'N/A'}
              </p>
            </div>
          </div>
          {submitResult.message_ids?.length > 0 && (
            <div className="px-5 py-3 flex items-center gap-2 flex-wrap" style={{ background: 'rgba(16, 185, 129, 0.02)', borderTop: '1px solid rgba(16, 185, 129, 0.1)' }}>
              <span className="text-[10px] font-bold text-gray-400 uppercase">Message IDs:</span>
              {submitResult.message_ids.map((id, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[11px] font-mono" style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#4f46e5' }}>{id}</span>
              ))}
            </div>
          )}
          <div className="px-5 py-3 flex justify-end" style={{ borderTop: '1px solid rgba(16, 185, 129, 0.08)' }}>
            <button type="button" onClick={handleFullReset} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Send className="w-3.5 h-3.5" /> Send Another
            </button>
          </div>
        </div>
      )}

      {/* ─── Error ─── */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{
          background: error.includes('Rate limit') ? 'rgba(245, 158, 11, 0.06)' : 'rgba(239, 68, 68, 0.06)',
          border: error.includes('Rate limit') ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(239, 68, 68, 0.15)',
        }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{
            background: error.includes('Rate limit') ? 'linear-gradient(135deg, #d97706, #f59e0b)' : 'linear-gradient(135deg, #dc2626, #ef4444)',
          }}>
            {error.includes('Rate limit') ? <Shield className="w-4 h-4 text-white" /> : <AlertCircle className="w-4 h-4 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: error.includes('Rate limit') ? '#92400e' : '#991b1b' }}>
              {error.includes('Rate limit') ? 'Rate Limit Exceeded' : 'Submission Failed'}
            </p>
            <p className="text-sm mt-0.5" style={{ color: error.includes('Rate limit') ? '#a16207' : '#b91c1c' }}>{error}</p>
          </div>
          <button type="button" onClick={() => setError('')} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* MAIN LAYOUT: Left (form) + Right (preview)         */}
      {/* ═══════════════════════════════════════════════════ */}
      {!success && (
        <div className="flex gap-5 items-start">

          {/* ──────── LEFT: Main Form ──────── */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* ═══ FIELD 1: Header (Sender ID) ═══ */}
            <div className="bg-white rounded-xl p-5" style={{
              ...cardStyle,
              borderLeft: selectedHeader ? '3px solid #10b981' : '3px solid #6366f1',
            }}>
              <RequiredLabel icon={Type} text="Header (Sender ID)" step="1" />
              {loadingHeaders ? (
                <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#6366f1' }} />
                  <span className="text-sm text-gray-500">Loading headers...</span>
                </div>
              ) : headersError ? (
                <div className="p-3 rounded-lg flex items-start gap-2" style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-amber-700">{headersError}</span>
                </div>
              ) : (
                <SearchableDropdown
                  options={headers.map(h => ({ value: h.id.toString(), label: `${h.header_name} (${h.header_type})` }))}
                  value={selectedHeader}
                  onChange={handleHeaderChange}
                  placeholder="Search & select header..."
                  clearable={false}
                  className={validationErrors.header ? 'border-red-300' : ''}
                />
              )}
              {validationErrors.header && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.header}</p>}
              {selectedHeaderObj && (
                <div className="mt-2 px-3 py-1.5 rounded-lg flex items-center gap-2" style={{ background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#059669' }} />
                  <span className="text-xs text-gray-600">Entity: <strong className="text-gray-800">{selectedHeaderObj.entity_name || selectedHeaderObj.entity_id}</strong></span>
                </div>
              )}
            </div>

            {/* ═══ FIELD 2: Template ═══ */}
            <div className="bg-white rounded-xl p-5" style={{
              ...cardStyle,
              borderLeft: selectedTemplate ? '3px solid #10b981' : '3px solid #6366f1',
            }}>
              <RequiredLabel icon={FileText} text="Template" step="2" />
              {!selectedHeader ? (
                <div className="py-3 px-4 rounded-lg text-sm text-gray-400" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px dashed rgba(99, 102, 241, 0.15)' }}>
                  Select a header first to see templates
                </div>
              ) : loadingTemplates ? (
                <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#6366f1' }} />
                  <span className="text-sm text-gray-500">Loading templates...</span>
                </div>
              ) : (
                <>
                  <SearchableDropdown
                    options={templates.map(t => ({ value: t.id.toString(), label: `${t.template_name} (${t.template_id})` }))}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    placeholder="Search & select template..."
                    clearable={false}
                    className={validationErrors.template ? 'border-red-300' : ''}
                  />
                  {templates.length === 0 && selectedHeader && !loadingTemplates && (
                    <p className="text-gray-400 text-xs mt-1.5">No templates found for this header</p>
                  )}
                </>
              )}
              {validationErrors.template && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.template}</p>}

              {/* Template Preview — inline read-only card */}
              {originalTemplate && (
                <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(99, 102, 241, 0.02)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Template Preview</span>
                    <span className="ml-auto text-[8px] font-bold uppercase px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-400">Read Only</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2 mb-2">
                    {[
                      { label: 'Name', value: templateName },
                      { label: 'Template ID', value: templateId, mono: true },
                      { label: 'Type', value: templateType },
                      { label: 'Variables', value: variables.length > 0 ? `${variables.length} var${variables.length > 1 ? 's' : ''}` : 'None', color: variables.length > 0 ? '#d97706' : '#059669' },
                      { label: 'SMS Parts', value: (() => { const len = originalTemplate.length; const isUni = /[^\x00-\x7F]/.test(originalTemplate); const cnt = isUni ? (len <= 70 ? 1 : Math.ceil(len / 67)) : (len <= 160 ? 1 : Math.ceil(len / 153)); return `${cnt} part${cnt > 1 ? 's' : ''}`; })(), color: (() => { const len = originalTemplate.length; const isUni = /[^\x00-\x7F]/.test(originalTemplate); return (isUni ? (len <= 70 ? 1 : Math.ceil(len / 67)) : (len <= 160 ? 1 : Math.ceil(len / 153))) > 1 ? '#d97706' : '#059669'; })() },
                    ].map(item => (
                      <div key={item.label} className="px-2 py-1 rounded bg-white" style={{ border: '1px solid rgba(99, 102, 241, 0.06)' }}>
                        <p className="text-[8px] font-bold text-gray-400 uppercase">{item.label}</p>
                        <p className={`text-[11px] font-semibold truncate ${item.mono ? 'font-mono' : ''}`} style={{ color: item.color || '#1e293b' }}>{item.value || '-'}</p>
                      </div>
                    ))}
                  </div>
                  <div className="px-2.5 py-2 rounded-lg text-xs leading-relaxed text-gray-600 whitespace-pre-wrap break-words bg-white" style={{ border: '1px solid rgba(99, 102, 241, 0.06)', maxHeight: '100px', overflowY: 'auto' }}>
                    {originalTemplate.split(DLT_VAR_REGEX).map((part, index) => {
                      const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                      const elements = [<span key={`t-${index}`}>{part}</span>];
                      if (index < tagMatches.length) {
                        const tag = tagMatches[index];
                        const tagInfo = DLT_VARIABLE_TAGS.find(t => t.tag === tag) || DLT_VARIABLE_TAGS[0];
                        elements.push(
                          <span key={`v-${index}`} className="inline-flex items-center mx-0.5 px-1 py-0.5 rounded text-[10px] font-bold" style={{ background: `${tagInfo.color}12`, color: tagInfo.color, border: `1px solid ${tagInfo.color}25` }}>
                            {tag}
                          </span>
                        );
                      }
                      return elements;
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ═══ FIELD 3: Message ═══ */}
            {originalTemplate && (
              <div className="bg-white rounded-xl p-5" style={{
                ...cardStyle,
                borderLeft: noUnfilledVars ? '3px solid #10b981' : '3px solid #6366f1',
              }}>
                <div className="flex items-center justify-between mb-3">
                  <RequiredLabel icon={MessageSquare} text="Message" step="3" />
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => navigator.clipboard.writeText(editableMessage)} className="px-2 py-1 rounded-lg text-[10px] font-medium text-gray-500 hover:bg-gray-50 flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button type="button" onClick={handleResetVariables} className="px-2 py-1 rounded-lg text-[10px] font-medium text-gray-500 hover:bg-gray-50 flex items-center gap-1" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left: Type / Paste */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <ClipboardPaste className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
                      <span className="text-[10px] font-bold text-gray-500 uppercase">Type or Paste</span>
                    </div>
                    <textarea
                      value={pasteText}
                      onChange={handlePasteTextChange}
                      rows={5}
                      className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed"
                      style={{ border: '1px solid rgba(99, 102, 241, 0.12)', minHeight: '140px' }}
                      placeholder="Type or paste your complete message here..."
                    />
                    {pasteError && (
                      <div className="mt-1 flex items-start gap-1 p-1.5 rounded" style={{ background: 'rgba(239, 68, 68, 0.06)' }}>
                        <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-[10px] text-red-600">{pasteError}</p>
                      </div>
                    )}
                  </div>

                  {/* Right: Live Preview */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Eye className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                      <span className="text-[10px] font-bold text-gray-500 uppercase">Live Preview</span>
                    </div>
                    <div className="relative">
                      <div
                        ref={overlayRef}
                        onPaste={handleDirectPaste}
                        className="px-3 py-2.5 rounded-lg overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 select-text"
                        style={{ minHeight: '140px', maxHeight: '200px', border: noUnfilledVars ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(99, 102, 241, 0.12)', background: noUnfilledVars ? 'rgba(16, 185, 129, 0.02)' : 'white' }}>
                        {(() => {
                          const result = [];
                          const textParts = originalTemplate.split(DLT_VAR_REGEX);
                          const tagMatches = originalTemplate.match(DLT_VAR_REGEX) || [];
                          let vi = 0;
                          textParts.forEach((part, index) => {
                            if (part) result.push(<span key={`text-${index}`}>{part}</span>);
                            if (index < tagMatches.length) {
                              const currentVi = vi;
                              const val = variableValues[currentVi] || '';
                              const varInfo = variables[currentVi] || {};
                              const tagColor = varInfo.color || '#d97706';
                              result.push(
                                <span key={`var-wrap-${index}`} className="inline-flex items-center mx-0.5" style={{ verticalAlign: 'baseline' }}>
                                  <span className="flex items-center rounded-md overflow-hidden" style={{
                                    border: val ? '1.5px solid rgba(16, 185, 129, 0.5)' : `1.5px dashed ${tagColor}60`,
                                    background: val ? 'rgba(16, 185, 129, 0.06)' : `${tagColor}08`,
                                  }}>
                                    <span className="px-1 py-0.5 text-[9px] font-bold select-none" style={{
                                      background: val ? 'rgba(16, 185, 129, 0.15)' : `${tagColor}18`,
                                      color: val ? '#059669' : tagColor,
                                      borderRight: val ? '1px solid rgba(16, 185, 129, 0.2)' : `1px solid ${tagColor}25`,
                                    }}>{currentVi + 1}</span>
                                    <input
                                      type="text"
                                      value={val}
                                      onChange={(e) => handleVariableChange(currentVi, e.target.value)}
                                      maxLength={30}
                                      placeholder={varInfo.label || `Var ${currentVi + 1}`}
                                      className="border-0 outline-none bg-transparent text-sm font-semibold px-1.5 py-0.5"
                                      style={{
                                        width: Math.max(70, Math.min(180, (val.length || 6) * 8 + 20)) + 'px',
                                        color: val ? '#059669' : '#9ca3af',
                                        caretColor: '#4f46e5',
                                      }}
                                    />
                                  </span>
                                </span>
                              );
                              vi++;
                            }
                          });
                          return result;
                        })()}
                      </div>
                      {pasteToast.show && (
                        <div className="absolute top-2 right-2 px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg z-10"
                          style={{ background: pasteToast.type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)', color: 'white' }}>
                          {pasteToast.type === 'success' ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                          {pasteToast.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats bar */}
                <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)' }}>
                  <span className="text-xs text-gray-500">Chars: <strong className="text-gray-800">{stats.length}</strong></span>
                  <span className="w-px h-3 bg-gray-200" />
                  <span className="text-xs text-gray-500">Parts: <strong style={{ color: stats.smsCount > 1 ? '#d97706' : '#059669' }}>{stats.smsCount}</strong></span>
                  <span className="w-px h-3 bg-gray-200" />
                  <span className="text-xs text-gray-500">{encodingType}</span>
                  {validationErrors.message && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.message}</span>}
                  {validationErrors.variables && <span className="text-red-500 text-xs ml-auto flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.variables}</span>}
                  {noUnfilledVars && !validationErrors.message && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-semibold" style={{ color: '#059669' }}><CheckCircle2 className="w-3 h-3" /> Ready</span>
                  )}
                </div>

                {/* Variable Cards */}
                {variables.length > 0 && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="w-3.5 h-3.5" style={{ color: '#d97706' }} />
                      <span className="text-[10px] font-bold text-gray-500 uppercase">Variables</span>
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706' }}>{variables.length}</span>
                      <span className="text-[9px] text-gray-400">Max 30 chars each</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {variables.map((varObj, index) => {
                        const tagColor = varObj.color || '#d97706';
                        const filled = variableValues[index] && variableValues[index].trim();
                        return (
                          <div key={`var_${index}`} className="relative rounded-lg p-2.5" style={{
                            background: filled ? 'rgba(16, 185, 129, 0.03)' : `${tagColor}04`,
                            border: filled ? '1px solid rgba(16, 185, 129, 0.15)' : `1px solid ${tagColor}15`,
                          }}>
                            <div className="flex items-center gap-1 mb-1">
                              <span className="w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold" style={{ background: filled ? 'rgba(16, 185, 129, 0.12)' : `${tagColor}15`, color: filled ? '#059669' : tagColor }}>{index + 1}</span>
                              <span className="px-1 py-0.5 rounded text-[8px] font-bold uppercase" style={{ background: `${tagColor}10`, color: tagColor }}>{varObj.tag}</span>
                              {filled && <CheckCircle2 className="w-3 h-3 ml-auto" style={{ color: '#059669' }} />}
                            </div>
                            <input
                              type="text"
                              value={variableValues[index] || ''}
                              onChange={(e) => handleVariableChange(index, e.target.value)}
                              maxLength={30}
                              className="w-full px-2 py-1.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
                              style={{ border: filled ? '1px solid rgba(16, 185, 129, 0.2)' : `1px solid ${tagColor}20` }}
                              placeholder={varObj.description || `Value ${index + 1}`}
                            />
                            <span className="absolute right-3 bottom-[10px] text-[9px] text-gray-300">{variableValues[index]?.length || 0}/30</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ FIELD 4: Recipients ═══ */}
            {originalTemplate && (
              <div className="bg-white rounded-xl p-5" style={{
                ...cardStyle,
                borderLeft: validNumbers.length > 0 ? '3px solid #10b981' : '3px solid #6366f1',
              }}>
                <div className="flex items-center justify-between mb-2">
                  <RequiredLabel icon={Phone} text="Recipients" step="4" />
                  <div className="flex items-center gap-2">
                    {validNumbers.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(16, 185, 129, 0.08)', color: '#059669' }}>
                        <CheckCircle className="w-3 h-3" /> {validNumbers.length} valid
                      </span>
                    )}
                    {invalidNumbers.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626' }}>
                        <XCircle className="w-3 h-3" /> {invalidNumbers.length} invalid
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">Indian mobile numbers (10 digits). One per line or comma-separated. Duplicates auto-removed.</p>
                <textarea
                  value={mobileNumbers}
                  onChange={handleMobileNumbersChange}
                  rows={4}
                  className="w-full px-3 py-2.5 font-mono text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                  style={{
                    border: validationErrors.mobile ? '1px solid #fca5a5' : validNumbers.length > 0 ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(99, 102, 241, 0.15)',
                    background: validNumbers.length > 0 ? 'rgba(16, 185, 129, 0.02)' : 'white',
                  }}
                  placeholder={"9876543210\n9123456789\n+919876543210"}
                />
                {validationErrors.mobile && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{validationErrors.mobile}</p>}
                {invalidNumbers.length > 0 && (
                  <div className="mt-2 p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    <p className="text-[10px] font-semibold text-red-600 mb-1">Invalid (will be skipped):</p>
                    <div className="flex flex-wrap gap-1">
                      {invalidNumbers.slice(0, 10).map((num, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-red-50 text-red-600 border border-red-100">{num}</span>
                      ))}
                      {invalidNumbers.length > 10 && <span className="text-[10px] text-red-400">+{invalidNumbers.length - 10} more</span>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ SEND BAR ═══ */}
            {originalTemplate && (
              <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: allReady ? 'rgba(16, 185, 129, 0.04)' : 'rgba(99, 102, 241, 0.03)', border: allReady ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid rgba(99, 102, 241, 0.08)' }}>
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Total: </span>
                    <span className="font-bold text-lg" style={{ color: '#4f46e5' }}>{stats.smsCount * validNumbers.length}</span>
                    <span className="text-gray-400 text-xs ml-1">SMS ({stats.smsCount} part{stats.smsCount > 1 ? 's' : ''} × {validNumbers.length} recipient{validNumbers.length !== 1 ? 's' : ''})</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handleFullReset}
                    className="px-3 py-2 rounded-lg text-xs font-medium text-gray-500 hover:bg-white" style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                    Reset
                  </button>
                  <button type="submit" disabled={loading || !allReady}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{ background: loading ? '#94a3b8' : allReady ? 'linear-gradient(135deg, #059669, #34d399)' : 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Send {validNumbers.length > 0 ? `${stats.smsCount * validNumbers.length} SMS` : 'SMS'}</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ──────── RIGHT: Sticky Phone Preview ──────── */}
          <div className="hidden xl:block flex-shrink-0 sticky top-4" style={{ width: '290px' }}>
            <MobilePreview
              message={editableMessage}
              senderName={selectedHeaderObj?.header_name}
              encodingType={encodingType}
              smsCount={stats.smsCount}
              charCount={stats.length}
            />
            {/* Quick summary below phone */}
            {originalTemplate && (
              <div className="mt-4 bg-white rounded-xl p-4" style={cardStyle}>
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
                  <span className="text-xs font-bold text-gray-700">Quick Summary</span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Header', value: selectedHeaderObj?.header_name || '-' },
                    { label: 'Template', value: templateName || '-' },
                    { label: 'Characters', value: stats.length },
                    { label: 'SMS Parts', value: stats.smsCount, color: stats.smsCount > 1 ? '#d97706' : '#059669' },
                    { label: 'Encoding', value: encodingType },
                    { label: 'Recipients', value: validNumbers.length, color: validNumbers.length > 0 ? '#059669' : '#94a3b8' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-500">{item.label}</span>
                      <span className="text-[11px] font-bold truncate ml-2" style={{ color: item.color || '#1e293b', maxWidth: '120px' }}>{item.value}</span>
                    </div>
                  ))}
                  <div className="pt-2 mt-1" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">Total SMS</span>
                      <span className="text-base font-bold" style={{ color: '#4f46e5' }}>{stats.smsCount * validNumbers.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

export default SendSMSPro;
